import { apiClient } from '@lib/axios';

import type {
  CreateSpecialtyRequest,
  UpdateSpecialtyRequest,
  Specialty,
  SpecialtyListData,
  SystemSpecialty,
  SystemSpecialtyListData,
  Treatment,
  TreatmentListData,
  CreateTreatmentRequest,
  UpdateTreatmentRequest,
} from '../types';

interface SpecialtyListEnvelope {
  success: boolean;
  data: Specialty[];
  pagination: { count: number; limit: number; nextToken: string | null };
  meta: { requestId: string };
}

interface SpecialtySingleEnvelope {
  success: boolean;
  data: Specialty;
  meta: { requestId: string };
}

interface SystemSpecialtyListEnvelope {
  success: boolean;
  data: SystemSpecialty[];
  pagination: { count: number; limit: number; nextToken: string | null };
  meta: { requestId: string };
}

export async function getSpecialties(accountId: string): Promise<SpecialtyListData> {
  const { data } = await apiClient.get<SpecialtyListEnvelope>(
    `/catalog/accounts/${accountId}/specialties`,
  );
  return { specialties: data.data, count: data.pagination.count };
}

export async function createSpecialty(
  accountId: string,
  body: CreateSpecialtyRequest,
): Promise<Specialty> {
  const { data } = await apiClient.post<SpecialtySingleEnvelope>(
    `/catalog/accounts/${accountId}/specialties`,
    body,
  );
  return data.data;
}

export async function updateSpecialty(
  accountId: string,
  specialtyId: string,
  body: UpdateSpecialtyRequest,
): Promise<Specialty> {
  const { data } = await apiClient.patch<SpecialtySingleEnvelope>(
    `/catalog/accounts/${accountId}/specialties/${specialtyId}`,
    body,
  );
  return data.data;
}

export async function deleteSpecialty(accountId: string, specialtyId: string): Promise<void> {
  await apiClient.delete(`/catalog/accounts/${accountId}/specialties/${specialtyId}`);
}

export async function getSystemSpecialties(lang: string): Promise<SystemSpecialtyListData> {
  const { data } = await apiClient.get<SystemSpecialtyListEnvelope>('/catalog/system/specialties', {
    params: { lang },
  });
  return { specialties: data.data, count: data.pagination.count };
}

// ─── Treatments ────────────────────────────────────────────────────────────────

interface TreatmentListEnvelope {
  success: boolean;
  data: Treatment[];
  pagination: { count: number; limit: number; nextToken: string | null };
  meta: { requestId: string };
}

interface TreatmentSingleEnvelope {
  success: boolean;
  data: Treatment;
  meta: { requestId: string };
}

export async function getTreatments(
  accountId: string,
  specialtyId: string,
): Promise<TreatmentListData> {
  const { data } = await apiClient.get<TreatmentListEnvelope>(
    `/catalog/accounts/${accountId}/specialties/${specialtyId}/treatments`,
  );
  return { treatments: data.data, count: data.pagination.count };
}

export async function createTreatment(
  accountId: string,
  specialtyId: string,
  body: CreateTreatmentRequest,
): Promise<Treatment> {
  const { data } = await apiClient.post<TreatmentSingleEnvelope>(
    `/catalog/accounts/${accountId}/specialties/${specialtyId}/treatments`,
    body,
  );
  return data.data;
}

export async function updateTreatment(
  accountId: string,
  specialtyId: string,
  treatmentId: string,
  body: UpdateTreatmentRequest,
): Promise<Treatment> {
  const { data } = await apiClient.patch<TreatmentSingleEnvelope>(
    `/catalog/accounts/${accountId}/specialties/${specialtyId}/treatments/${treatmentId}`,
    body,
  );
  return data.data;
}
